import React, { useEffect, useState } from 'react';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';

const StarRating = ({ value }) => (
  <div className="d-flex align-items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <i
        key={i}
        className={`bi bi-star${i <= value ? '-fill' : ''} text-warning`}
        style={{ fontSize: 15 }}
      ></i>
    ))}
    <span className="text-muted ms-1 fs-7">({value}/5)</span>
  </div>
);

const CustomerReviews = ({ userId }) => {
  const [isLoader, setIsLoader] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('latest');

  const getReviews = async () => {
    try {
      setIsLoader(true);
      const params = new URLSearchParams({ page, limit: 10, sort_by: sortBy });
      const res = await ApiService.request({ method: 'GET', url: `admin/users/${userId}/reviews?${params}` });
      if (res.data.status) {
        setReviews(res.data.data || []);
        setPagination(res.data.pagination || {});
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => { getReviews(); }, [userId, page, sortBy]); // eslint-disable-line

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <h3 className="card-title fw-bold m-0">Reviews</h3>
        <div className="card-toolbar">
          <select className="form-select" style={{ width: 200 }} value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>
      </div>

      <div className="card-body pt-4">
        {isLoader && reviews.length === 0 ? (
          <div className="text-center py-10"><Loader text="Loading reviews…" size={30} /></div>
        ) : reviews.length === 0 ? (
          <div className="text-center text-muted py-10">
            <i className="bi bi-star fs-2x mb-3 d-block"></i>
            <p className="mb-0">No reviews found</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-4">
            {reviews.map((rv) => (
              <div key={rv._id} className="border rounded-3 p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-3">
                    {rv.partner_id?.logo ? (
                      <img src={rv.partner_id.logo} alt={rv.partner_id.company_name} className="rounded-circle" style={{ width: 44, height: 44, objectFit: 'cover' }} />
                    ) : (
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, background: '#fdf8f9', border: '1px solid #f0e0e4' }}>
                        <i className="bi bi-building" style={{ color: '#c4941a' }}></i>
                      </div>
                    )}
                    <div>
                      <div className="fw-bold text-gray-800">{rv.partner_id?.company_name || 'N/A'}</div>
                      {rv.booking_id?.booking_code && (
                        <div className="text-muted fs-7">{rv.booking_id.booking_code}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-muted fs-7">
                    {rv.createdAt ? new Date(rv.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                  </div>
                </div>

                <StarRating value={rv.stars || 0} />

                {rv.review && (
                  <p className="text-gray-700 fs-6 mt-3 mb-0">{rv.review}</p>
                )}

                <div className="mt-2">
                  <span className={`badge bg-${rv.status === 'Approved' ? 'success' : rv.status === 'Rejected' ? 'danger' : 'warning'}`}>
                    {rv.status || 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-5">
            <span className="text-muted fs-7">{reviews.length} of {pagination.total_records} reviews</span>
            <Paginator page={pagination.current_page} rows={pagination.per_page} totalRecords={pagination.total_records} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerReviews;
